import { useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { authApi } from '../api/client'
import { useAuth } from '../context/AuthContext'
import { TermsContent } from './TermsPage'
import LanguageSwitcher from '../components/LanguageSwitcher'

const buildSchema = (t: TFunction) =>
  z
    .object({
      userId: z.string().min(4, t('auth.register.userIdMin')).max(50),
      email: z.string().email(t('auth.register.emailInvalid')),
      password: z.string().min(8, t('auth.register.passwordMin')),
      passwordConfirm: z.string(),
      name: z.string().min(1, t('auth.register.nameRequired')),
      organization: z.string().optional(),
      termsAgreed: z.boolean().refine((v) => v === true, { message: t('auth.register.termsRequired') }),
    })
    .refine((d) => d.password === d.passwordConfirm, {
      message: t('auth.register.passwordMismatch'),
      path: ['passwordConfirm'],
    })
type FormData = z.infer<ReturnType<typeof buildSchema>>

export default function RegisterPage() {
  const { t, i18n } = useTranslation()
  const schema = useMemo(() => buildSchema(t), [t])
  const navigate = useNavigate()
  const { login } = useAuth()
  const [error, setError] = useState('')
  const [userIdChecked, setUserIdChecked] = useState(false)
  const [userIdAvailable, setUserIdAvailable] = useState<boolean | null>(null)
  const [checkingUserId, setCheckingUserId] = useState(false)
  const [showTerms, setShowTerms] = useState(false)

  const { register, handleSubmit, getValues, watch, setValue, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { termsAgreed: false },
  })

  const userIdValue = watch('userId')

  const handleUserIdChange = () => {
    setUserIdChecked(false)
    setUserIdAvailable(null)
  }

  const handleCheckUserId = async () => {
    const userId = getValues('userId')
    if (!userId || userId.length < 4) return
    setCheckingUserId(true)
    try {
      const available = await authApi.checkUserId(userId)
      setUserIdAvailable(available)
      setUserIdChecked(true)
    } catch {
      setUserIdAvailable(null)
    } finally {
      setCheckingUserId(false)
    }
  }

  const onSubmit = async (data: FormData) => {
    if (!userIdChecked || !userIdAvailable) {
      setError(t('auth.register.checkUserIdFirst'))
      return
    }
    setError('')
    try {
      const res = await authApi.register({
        userId: data.userId,
        email: data.email,
        password: data.password,
        name: data.name,
        organization: data.organization,
      })
      login(res.token, { userId: res.userId, name: res.name, email: res.email, userLevel: res.userLevel ?? 'FREE', admin: res.admin ?? false })
      navigate('/dashboard', { replace: true })
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } }
      setError(err.response?.data?.error ?? t('auth.register.failed'))
    }
  }

  const otherFields: Array<{ key: keyof FormData; label: string; type?: string; placeholder: string }> = [
    { key: 'email', label: t('auth.register.email'), type: 'email', placeholder: 'example@email.com' },
    { key: 'name', label: t('auth.register.name'), placeholder: t('auth.register.namePlaceholder') },
    { key: 'organization', label: t('auth.register.organization'), placeholder: t('auth.register.organizationPlaceholder') },
    { key: 'password', label: t('auth.register.password'), type: 'password', placeholder: t('auth.register.passwordPlaceholder') },
    { key: 'passwordConfirm', label: t('auth.register.passwordConfirm'), type: 'password', placeholder: t('auth.register.passwordConfirmPlaceholder') },
  ]

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <LanguageSwitcher className="absolute right-4 top-4" />
      <div className="max-w-md w-full space-y-8">
        <div>
          <h1 className="text-center text-3xl font-extrabold text-gray-900">{t('auth.register.brand')}</h1>
          <h2 className="mt-2 text-center text-sm text-gray-600">Reality Mesh Service</h2>
        </div>

        <form className="mt-8 space-y-6 bg-white p-8 rounded-xl shadow" onSubmit={handleSubmit(onSubmit)}>
          <h3 className="text-xl font-bold text-gray-800">{t('auth.register.title')}</h3>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">{error}</div>
          )}

          <div className="space-y-4">
            {/* 아이디 + 중복 확인 */}
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('auth.register.userId')}</label>
              <div className="flex gap-2 mt-1">
                <input
                  {...register('userId', { onChange: handleUserIdChange })}
                  type="text"
                  placeholder={t('auth.register.userIdPlaceholder')}
                  className="input-field flex-1"
                />
                <button
                  type="button"
                  onClick={handleCheckUserId}
                  disabled={checkingUserId || !userIdValue || userIdValue.length < 4}
                  className="px-3 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {checkingUserId ? t('auth.register.checkingUserId') : t('auth.register.checkUserId')}
                </button>
              </div>
              {errors.userId && <p className="form-error">{errors.userId.message}</p>}
              {userIdChecked && userIdAvailable === true && (
                <p className="mt-1 text-xs text-green-600">{t('auth.register.userIdAvailable')}</p>
              )}
              {userIdChecked && userIdAvailable === false && (
                <p className="mt-1 text-xs text-red-600">{t('auth.register.userIdTaken')}</p>
              )}
            </div>

            {otherFields.map(({ key, label, type = 'text', placeholder }) => (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700">{label}</label>
                <input
                  {...register(key)}
                  type={type}
                  placeholder={placeholder}
                  className="input-field mt-1"
                />
                {errors[key] && <p className="form-error">{errors[key]?.message}</p>}
              </div>
            ))}
          </div>

          {/* 이용약관 동의 */}
          <div className="space-y-1">
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="termsAgreed"
                {...register('termsAgreed')}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
              />
              <label htmlFor="termsAgreed" className="text-sm text-gray-700 cursor-pointer select-none">
                <Trans
                  i18nKey="auth.register.termsAgree"
                  components={{
                    terms: (
                      <button
                        type="button"
                        onClick={() => setShowTerms(true)}
                        className="font-medium text-primary-600 hover:underline"
                      />
                    ),
                  }}
                />
              </label>
            </div>
            {errors.termsAgreed && (
              <p className="form-error">{errors.termsAgreed.message}</p>
            )}
          </div>

          <button type="submit" disabled={isSubmitting} className="btn-primary">
            {isSubmitting ? t('auth.register.submitting') : t('auth.register.submit')}
          </button>

          <p className="text-center text-sm text-gray-600">
            {t('auth.register.haveAccount')}{' '}
            <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
              {t('auth.register.login')}
            </Link>
          </p>
        </form>
      </div>

      {/* 이용약관 모달 */}
      {showTerms && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowTerms(false)}
        >
          <div
            className="bg-white rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
              <div>
                <h2 className="text-base font-bold text-gray-900">{t('auth.register.termsTitle')}</h2>
                <p className="text-xs text-gray-400 mt-0.5">{t('auth.register.termsCompany')}</p>
              </div>
              <button
                type="button"
                onClick={() => setShowTerms(false)}
                className="text-gray-400 hover:text-gray-700 text-xl leading-none"
                aria-label={t('common.close')}
              >
                ✕
              </button>
            </div>
            <div className="overflow-y-auto px-6 py-5 flex-1">
              {i18n.language !== 'ko' && (
                <p className="mb-4 rounded bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  {t('auth.register.termsKoreanOnly')}
                </p>
              )}
              <TermsContent />
            </div>
            <div className="px-6 py-4 border-t border-gray-200 shrink-0 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowTerms(false)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {t('auth.register.termsClose')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setValue('termsAgreed', true, { shouldValidate: true })
                  setShowTerms(false)
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md"
              >
                {t('auth.register.termsAgreeClose')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
